/**
 * 液化位移场（ADR-0013）。
 *
 * 会话期间的可变形预览状态——**不进 TileStore**：位移场不是像素，是
 * "确定"之前的中间数据，不需要 COW/驱逐/脏区这套面向已提交像素的体系。
 * 按 tile 稀疏存储（`Map<string, Float32Array>`，键同 `engine/brush/buffer.ts`
 * 的 `StrokeBuffer`），只在笔刷实际经过的块惰性分配，跟"空白文档应为 0
 * 字节"这条项目核心不变量保持一致——整图尺寸的扁平数组对大画布是这条
 * 不变量的反例（`engine/tile/store.ts` 文件头注释点名过这类问题）。
 *
 * 数学模型是逐 stamp 直接线性叠加，不是网格 advection：大幅度重叠多笔
 * 会有轻微失真累积，不做"重建"画笔去修复——这是相对完整 Photoshop 液化
 * 的已知简化，写在这里也写进用户可见的功能说明里。
 */
import { TILE } from '../tile/key';

export interface Point {
  readonly x: number;
  readonly y: number;
}

export interface Rect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/** 软刷衰减：C¹ 连续，t>=1 归零。 */
function falloff(t: number): number {
  if (t >= 1) return 0;
  const s = 1 - t * t;
  return s * s;
}

/** 每个候选像素得到的位移增量（文档空间）。 */
type DeltaFn = (offsetX: number, offsetY: number, weight: number) => readonly [number, number];

export class DisplacementField {
  /** 键 "tx ty"，值长度 TILE*TILE*2（每像素 dx,dy 交错）。 */
  readonly #tiles = new Map<string, Float32Array>();
  readonly #docWidth: number;
  readonly #docHeight: number;
  #dirty: Rect | null = null;
  #total: Rect | null = null;
  /**
   * 会话内出现过的最大位移幅度（|dx,dy| 的 hypot）。推模式的位移量不像
   * 扭转/收缩膨胀那样天然被 `radius` 约束（`v=(to-from)*strength`，拖动
   * 距离和强度都可能让它远超笔刷半径）——采样/回读源像素的矩形必须按
   * 这个真实幅度加padding，否则会在"半径算出来的小矩形"边缘读到矩形外
   * （但仍在图层范围内）的有效像素时误判成"越界"而返回透明，画面上就是
   * 一块黑洞（这是这条字段存在的直接原因，不是顺手加的）。
   */
  #maxMagnitude = 0;

  constructor(docWidth: number, docHeight: number) {
    this.#docWidth = docWidth;
    this.#docHeight = docHeight;
  }

  get docWidth(): number {
    return this.#docWidth;
  }

  get docHeight(): number {
    return this.#docHeight;
  }

  isEmpty(): boolean {
    return this.#tiles.size === 0;
  }

  /** 上一次 apply* 调用触碰的外接矩形（供 UI 层只重画这一小块）。 */
  dirtyBounds(): Rect | null {
    return this.#dirty;
  }

  /** 会话开始以来全部 apply* 调用的外接矩形（供 commit 时决定要 touch 哪些 tile）。 */
  totalBounds(): Rect | null {
    return this.#total;
  }

  /** 会话内任意一点累积位移的最大幅度——采样源区域时的必要 padding 依据。 */
  maxMagnitude(): number {
    return this.#maxMagnitude;
  }

  /** 读一点的累积位移（测试与预览用）。未分配的 tile 恒为 [0,0]。 */
  at(x: number, y: number): readonly [number, number] {
    const tx = Math.floor(x / TILE);
    const ty = Math.floor(y / TILE);
    const tile = this.#tiles.get(`${tx} ${ty}`);
    if (tile === undefined) return [0, 0];
    const at = ((y - ty * TILE) * TILE + (x - tx * TILE)) * 2;
    return [tile[at]!, tile[at + 1]!];
  }

  /** 推：沿拖拽向量 `to-from`，按笔刷中心 `to`、半径 `radius` 衰减，强度 `strength`。 */
  applyPush(from: Point, to: Point, radius: number, strength: number): void {
    const vx = (to.x - from.x) * strength;
    const vy = (to.y - from.y) * strength;
    this.#accumulate(to.x, to.y, radius, (_ox, _oy, w) => [vx * w, vy * w]);
  }

  /** 扭转：绕 `center` 旋转 `angleStep` 弧度（正=顺时针，传负值得逆时针）。 */
  applyTwirl(center: Point, radius: number, angleStep: number): void {
    this.#accumulate(center.x, center.y, radius, (ox, oy, w) => {
      const angle = angleStep * w;
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      return [ox * (1 - cos) + oy * sin, -ox * sin + oy * (1 - cos)];
    });
  }

  /** 收缩/膨胀：`amount` 正=膨胀负=收缩，中心点恒不动（自洽）。 */
  applyPuckerBloat(center: Point, radius: number, amount: number): void {
    this.#accumulate(center.x, center.y, radius, (ox, oy, w) => [amount * w * ox, amount * w * oy]);
  }

  #accumulate(cx: number, cy: number, radius: number, delta: DeltaFn): void {
    if (radius <= 0) return;
    const x0 = Math.max(0, Math.floor(cx - radius));
    const y0 = Math.max(0, Math.floor(cy - radius));
    const x1 = Math.min(this.#docWidth - 1, Math.ceil(cx + radius));
    const y1 = Math.min(this.#docHeight - 1, Math.ceil(cy + radius));
    if (x1 < x0 || y1 < y0) return;

    let touched = false;
    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) {
        const ox = x + 0.5 - cx;
        const oy = y + 0.5 - cy;
        const t = Math.hypot(ox, oy) / radius;
        const w = falloff(t);
        if (w <= 0) continue;
        const [dx, dy] = delta(ox, oy, w);
        this.#addAt(x, y, dx, dy, radius);
        touched = true;
      }
    }
    if (!touched) return;

    const rect: Rect = { x: x0, y: y0, width: x1 - x0 + 1, height: y1 - y0 + 1 };
    this.#dirty = rect;
    this.#total = this.#total === null ? rect : union(this.#total, rect);
  }

  /**
   * 累加一次位移增量，并把**累计后**的幅度夹到 `maxMagnitude` 以内。
   *
   * 没有这个夹逼，一条拖拽手势里连续多次 pointermove 各自产生一个 stamp
   * （半径远大于两次采样的间距时严重重叠）会在同一批像素上反复叠加各自
   * 的位移向量——推模式的单个 stamp 位移量由鼠标速度决定、不像扭转/
   * 收缩膨胀那样天然被几何半径钳制，叠加几次就能让某块像素的位移远超
   * 笔刷本身的半径，采样点被甩到图层实际内容之外，画面上就是一块黑洞
   * （用户反馈"推动后会出现黑色区域"的根因）。用当前 stamp 的半径当夹逼
   * 上限，是"一个点不会被推得比笔刷本身还远"这条朴素、跟扭转/收缩膨胀
   * 天然满足的约束保持一致，不是专门为了压 bug 拍的数字。
   */
  #addAt(x: number, y: number, dx: number, dy: number, maxMagnitude: number): void {
    const tx = Math.floor(x / TILE);
    const ty = Math.floor(y / TILE);
    const id = `${tx} ${ty}`;
    let tile = this.#tiles.get(id);
    if (tile === undefined) {
      tile = new Float32Array(TILE * TILE * 2);
      this.#tiles.set(id, tile);
    }
    const at = ((y - ty * TILE) * TILE + (x - tx * TILE)) * 2;
    let nx = tile[at]! + dx;
    let ny = tile[at + 1]! + dy;
    const magnitude = Math.hypot(nx, ny);
    if (magnitude > maxMagnitude && magnitude > 0) {
      const scale = maxMagnitude / magnitude;
      nx *= scale;
      ny *= scale;
    }
    tile[at] = nx;
    tile[at + 1] = ny;
    const finalMagnitude = Math.hypot(nx, ny);
    if (finalMagnitude > this.#maxMagnitude) this.#maxMagnitude = finalMagnitude;
  }

  /**
   * 把 `bounds` 覆盖的位移展平进调用方提供的缓冲（长度必须为
   * `bounds.width*bounds.height`，dx/dy 分开两个数组，与
   * `warp_field_bilinear_rgba8` 的参数形状一致）。未分配的 tile 视为全零。
   */
  flattenTo(dx: Float32Array, dy: Float32Array, bounds: Rect): void {
    const need = bounds.width * bounds.height;
    if (dx.length !== need || dy.length !== need) {
      throw new RangeError(`展平缓冲长度必须为 ${need}，收到 dx=${dx.length} dy=${dy.length}。`);
    }
    for (let y = 0; y < bounds.height; y++) {
      const docY = bounds.y + y;
      const ty = Math.floor(docY / TILE);
      for (let x = 0; x < bounds.width; x++) {
        const docX = bounds.x + x;
        const tx = Math.floor(docX / TILE);
        const tile = this.#tiles.get(`${tx} ${ty}`);
        if (tile === undefined) continue;
        const from = ((docY - ty * TILE) * TILE + (docX - tx * TILE)) * 2;
        const to = y * bounds.width + x;
        dx[to] = tile[from]!;
        dy[to] = tile[from + 1]!;
      }
    }
  }
}

function union(a: Rect, b: Rect): Rect {
  const x0 = Math.min(a.x, b.x);
  const y0 = Math.min(a.y, b.y);
  const x1 = Math.max(a.x + a.width, b.x + b.width);
  const y1 = Math.max(a.y + a.height, b.y + b.height);
  return { x: x0, y: y0, width: x1 - x0, height: y1 - y0 };
}

/**
 * 把 `rect` 向四周扩张 `margin` 像素，裁到 `[0,docWidth)x[0,docHeight)`。
 * warp 需要在这块扩张后的区域里采样源像素——`margin` 应该取
 * `field.maxMagnitude()`（向上取整+1），否则矩形边缘的输出像素会去读
 * 矩形外但仍在文档范围内的有效像素，被误判成"越界"而返回透明，
 * 画面上就是一块黑洞。
 */
export function padRect(rect: Rect, margin: number, docWidth: number, docHeight: number): Rect {
  const x0 = Math.max(0, Math.floor(rect.x - margin));
  const y0 = Math.max(0, Math.floor(rect.y - margin));
  const x1 = Math.min(docWidth, Math.ceil(rect.x + rect.width + margin));
  const y1 = Math.min(docHeight, Math.ceil(rect.y + rect.height + margin));
  return { x: x0, y: y0, width: Math.max(0, x1 - x0), height: Math.max(0, y1 - y0) };
}
